import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {Popover} from 'primeng/popover';

/**
 * Data describing the attribute/collection/join for which copy options are shown.
 */
export interface TeCopyAttrData {
    path: string;
    label: string;
    type: 'attribute' | 'collection' | 'join';
    dataType?: string;
    itemAlias?: string;
    values?: string[];
}

/**
 * A single item in the copy popover menu.
 */
export interface CopyItem {
    icon?: string;
    label?: string;
    code?: string;
    codePreview?: string;
    separator?: boolean;
    header?: string;
    flashActive?: boolean;
}

/**
 * Copy Popover Component.
 *
 * Displays a context-aware popover with copy-to-clipboard options
 * for FreeMarker variable expressions, loops, and conditionals.
 *
 * The popover stays open after copying so the user can copy multiple items.
 * Each clicked item flashes green briefly to confirm the copy.
 *
 * Usage:
 *   <mvs-te-copy-popover #copyPop [attrData]="selectedAttr"></mvs-te-copy-popover>
 *
 *   <!-- Trigger from parent: -->
 *   this.copyPop.show(event, attrData);
 */
@Component({
    selector: 'mvs-te-copy-popover',
    templateUrl: './te-copy-popover.component.html',
    styleUrls: ['./te-copy-popover.component.scss'],
    standalone: false
})
export class TeCopyPopoverComponent implements OnChanges {

    @Input() attrData: TeCopyAttrData;

    @ViewChild('copyPopover', {static: false}) popoverRef: Popover;

    copyItems: CopyItem[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['attrData'] && this.attrData) {
            this.buildCopyItems();
        }
    }

    /**
     * Public method to open the popover and set the attribute data.
     * @param event The DOM event (click) that triggered the popover
     * @param data The attribute data for which to show copy options
     */
    show(event: Event, data: TeCopyAttrData): void {
        this.attrData = data;
        this.buildCopyItems();

        if (this.popoverRef) {
            this.popoverRef.toggle(event);
        }
    }

    /**
     * Build the list of copy items based on the attribute data type.
     */
    buildCopyItems(): void {
        this.copyItems = [];

        if (!this.attrData) {
            return;
        }

        const path = this.attrData.path;
        const alias = this.attrData.itemAlias || 'item';

        switch (this.attrData.type) {

            case 'collection':
                this.copyItems.push({
                    icon: 'fa-regular fa-arrows-rotate',
                    label: 'LOOP',
                    code: `<#list ${path} as ${alias}>\n  ...\n</#list>`,
                    codePreview: `<#list ${path} as ${alias}>...`
                });
                this.copyItems.push({
                    icon: 'fa-regular fa-arrows-rotate',
                    label: 'LOOP mit Tabelle',
                    code: `<#list ${path} as ${alias}>\n<table>\n  <tr>\n    <td>...</td>\n  </tr>\n</table>\n</#list>`,
                    codePreview: `<#list ...><table>...`
                });
                this.copyItems.push({separator: true});
                this.copyItems.push({
                    icon: 'fa-regular fa-circle-question',
                    label: 'Wenn vorhanden',
                    code: `<#if ${path}?? && (${path}?size > 0)>\n  ...\n</#if>`,
                    codePreview: `<#if ${path}?? && ...>`
                });
                this.copyItems.push({
                    icon: 'fa-regular fa-clipboard',
                    label: 'Anzahl',
                    code: `\${${path}?size}`,
                    codePreview: `\${${path}?size}`
                });
                break;

            case 'join':
                this.copyItems.push({
                    icon: 'fa-regular fa-clipboard',
                    label: 'Zugriffspfad',
                    code: path,
                    codePreview: path
                });
                this.copyItems.push({
                    icon: 'fa-regular fa-circle-question',
                    label: 'Wenn vorhanden',
                    code: `<#if ${path}??>\n  ...\n</#if>`,
                    codePreview: `<#if ${path}??>...`
                });
                break;

            case 'attribute':
                this.copyItems.push({
                    icon: 'fa-regular fa-clipboard',
                    label: 'Variable',
                    code: `\${${path}}`,
                    codePreview: `\${${path}}`
                });
                this.copyItems.push({
                    icon: 'fa-regular fa-circle-question',
                    label: 'Wenn vorhanden',
                    code: `<#if ${path}??>\n  \${${path}}\n</#if>`,
                    codePreview: `<#if ${path}??>...`
                });
                this.copyItems.push({
                    icon: 'fa-regular fa-code-branch',
                    label: 'Wenn / Sonst',
                    code: `<#if ${path}??>\n  \${${path}}\n<#else>\n  -\n</#if>`,
                    codePreview: `<#if ${path}??>...<#else>...`
                });

                // Boolean-specific option
                if (this.attrData.dataType === 'boolean') {
                    this.copyItems.push({
                        icon: 'fa-regular fa-circle-check',
                        label: 'Wenn Wahr / Sonst',
                        code: `<#if ${path}>\n  ...wenn wahr...\n<#else>\n  ...wenn falsch...\n</#if>`,
                        codePreview: `<#if ${path}>...wahr...<#else>...`
                    });
                }

                // Enum-specific options
                if (this.attrData.dataType === 'enum' && this.attrData.values && this.attrData.values.length > 0) {
                    this.copyItems.push({separator: true});
                    for (const value of this.attrData.values) {
                        this.copyItems.push({
                            icon: 'fa-regular fa-equals',
                            label: `Wenn = "${value}"`,
                            code: `<#if ${path} == "${value}">\n  ...\n<#else>\n  ...\n</#if>`,
                            codePreview: `<#if ${path} == "${value}">...`
                        });
                    }
                }

                // String / currency / date — equals empty string
                if (this.attrData.dataType === 'string' || this.attrData.dataType === 'currency' || this.attrData.dataType === 'date') {
                    this.copyItems.push({
                        icon: 'fa-regular fa-equals',
                        label: 'Wenn gleich ""',
                        code: `<#if ${path} == "">\n  ...\n<#else>\n  ...\n</#if>`,
                        codePreview: `<#if ${path} == "">...`
                    });
                }
                break;
        }
    }

    /**
     * Copy the code to the clipboard and show a brief green flash feedback.
     * @param item The copy item whose code to copy
     */
    copyToClipboard(item: CopyItem): void {
        if (!item.code) {
            return;
        }

        navigator.clipboard.writeText(item.code).then(() => {
            this.showCopyFeedback(item);
        }).catch(() => {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = item.code;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            this.showCopyFeedback(item);
        });
    }

    /**
     * Show a brief green flash on the copied item, then close the popover.
     */
    private showCopyFeedback(item: CopyItem): void {
        item.flashActive = true;
        setTimeout(() => {
            item.flashActive = false;
            if (this.popoverRef) {
                this.popoverRef.hide();
            }
        }, 400);
    }
}
